import { css } from "@compiled/react";

import { TemplatePreviewWindow } from "@/components/confluence/templates/template-preview-window";
import { getTemplateType } from "@/components/confluence/templates/util/template-util";
import {
    Image,
    Template,
    TemplateType
} from "@/content/templates/template.types";

import { PreviewWave } from "../graphics/preview-wave";

export interface DeliveryApiImage {
    title: string;
    description: string;
    file: {
        url: string;
        details: {
            size: number;
            image: {
                width: number;
                height: number;
            };
        };
        fileName: string;
        contentType: string;
    };
}

interface TemplatePreviewSectionProps {
    templatePreview?: Image | DeliveryApiImage;
    template?: Template;
    hideBackground?: boolean;
    omitPadding?: boolean;
    fullWidth?: boolean;
}

const templatePreviewSection = css({
    textAlign: "center",
    position: "relative"
});

const templatePreviewSectionPadding = css({
    paddingBlockEnd: 64,
    paddingInlineEnd: 20,
    paddingInlineStart: 20
});

const previewBackground = css({
    "position": "absolute",
    "justifyContent": "center",
    "overflow": "hidden",
    "insetInlineEnd": 0,
    "insetInlineStart": 0,
    "insetBlockEnd": 0,

    "@media (max-width: 39.99rem)": {
        display: "grid"
    }
});
const relativePosition = css({
    position: "relative",
    margin: "0 auto"
});

const widthStepwiseCss = css({
    "@media (max-width: 39.99rem)": {
        maxWidth: 288
    },
    "@media (min-width: 40rem) and (max-width: 767.99px)": {
        maxWidth: 568
    },
    "@media (min-width: 768px) and (max-width: 1023.99px)": {
        maxWidth: 688
    },
    "@media (min-width: 1024px) and (max-width: 1279.99px)": {
        maxWidth: 648
    }
});

function isDeliveryApiImage(
    value: Image | DeliveryApiImage
): value is DeliveryApiImage {
    return value && typeof value === "object" && "file" in value;
}

// eslint-disable-next-line complexity
export const TemplatePreviewSection = ({
    template,
    templatePreview: templatePreviewProp,
    hideBackground,
    omitPadding,
    fullWidth
}: TemplatePreviewSectionProps) => {
    const templatePreview =
        template?.templatePreview?.[0] ?? templatePreviewProp ?? null;

    if (!templatePreview) {
        return null;
    }

    return (
        <section
            css={[
                templatePreviewSection,
                !omitPadding && templatePreviewSectionPadding
            ]}
        >
            <div css={previewBackground}>
                {!hideBackground && <PreviewWave />}
            </div>
            <div css={[relativePosition, !fullWidth && widthStepwiseCss]}>
                <TemplatePreviewWindow
                    url={
                        isDeliveryApiImage(templatePreview)
                            ? templatePreview.file.url
                            : templatePreview.url ?? ""
                    }
                    alt={template?.name ?? templatePreview?.description ?? ""}
                    templateType={
                        template
                            ? getTemplateType(template).type
                            : TemplateType.Page
                    }
                />
            </div>
        </section>
    );
};
